
import { create } from 'zustand';
import moment from 'moment-timezone';
import { updateUser, updateUserProfile, updateUserAuth, getUserById, uploadAvatar, updateNotificationSettings } from '../reducers/user/resources';
import { getUserSettings, updateOfficeHours } from '../reducers/integrations/resources';
import resources from '../reducers/login/resources';
import session from '../utils/session';
import format from '../utils/format';
import constants from '../constants';

const INVERTED_MSG = 'End time must be after start time';

const isInverted = (start, end) => {
  if (!start || !end) return false;
  const s = start.hour() * 60 + start.minute();
  const e = end.hour() * 60 + end.minute();
  return e <= s;
};

// dayErrors: array aligned to availabilitySched indices (custom mode), entry = msg | null
// workingHoursError: msg | null for the everyday/weekdays Working Hours pair
export const getOfficeHoursErrors = ({ scheduleMode, availabilitySched, notCustomStartTime, notCustomEndTime }) => {
  if (scheduleMode === 'custom') {
    const dayErrors = availabilitySched.map((day) =>
      (day.isActive && isInverted(day.startTime, day.endTime)) ? INVERTED_MSG : null);
    return { dayErrors, workingHoursError: null, hasError: dayErrors.some(Boolean) };
  }
  const workingHoursError = isInverted(notCustomStartTime, notCustomEndTime) ? INVERTED_MSG : null;
  return { dayErrors: [], workingHoursError, hasError: !!workingHoursError };
};

const useProfileStore = create((set, get) => ({
  // Profile State
  loading: false,
  userId: '',
  initials: 'U',
  avatarUrl: '',
  avatarType: 'preset',
  avatarSaving: false,
  uploading: false,
  uploadProgress: 0,
  showPresets: false,

  // TFA State
  tfaRequestModal: false,
  tfaCheckModal: false,
  tfaMobile: '',
  tfaCode: '',
  tfaRequestId: '',
  tfaLoading: false,
  resetTfaTimer: false,
  refTfaOption: false,

  // Office Hours State
  selectedDays: [false, true, true, true, true, true, false],
  startTime: moment('09:00', 'HH:mm'),
  endTime: moment('17:00', 'HH:mm'),
  timezone: '',
  acknowledgmentEnabled: false,
  acknowledgmentMsg: '',
  scheduleMode: 'everyday',
  availabilitySched: [],
  notCustomStartTime: moment('09:00', 'HH:mm'),
  notCustomEndTime: moment('17:00', 'HH:mm'),
  officeHoursLoading: false,

  // Actions
  setUserId: (id) => set({ userId: id }),
  setInitials: (initials) => set({ initials }),
  setAvatarUrl: (url) => set({ avatarUrl: url }),
  setAvatarType: (type) => set({ avatarType: type }),
  setShowPresets: (show) => set({ showPresets: show }),
  setTfaRequestModal: (open) => set({ tfaRequestModal: open }),
  setTfaCheckModal: (open) => set({ tfaCheckModal: open }),
  setTfaMobile: (mobile) => set({ tfaMobile: mobile }),
  setTfaCode: (code) => set({ tfaCode: code }),
  setResetTfaTimer: (reset) => set({ resetTfaTimer: reset }),
  setRefTfaOption: (option) => set({ refTfaOption: option }),
  
  // Office Hours Actions
  setSelectedDays: (days) => set({ selectedDays: days }),
  setStartTime: (time) => set({ startTime: time }),
  setEndTime: (time) => set({ endTime: time }),
  setTimezone: (zone) => set({ timezone: zone }),
  setAcknowledgmentEnabled: (enabled) => set({ acknowledgmentEnabled: enabled }),
  setAcknowledgmentMsg: (msg) => set({ acknowledgmentMsg: msg }),
  setScheduleMode: (mode) => set({ scheduleMode: mode }),
  setAvailabilitySched: (sched) => set({ availabilitySched: sched }),
  setNotCustomStartTime: (time) => set({ notCustomStartTime: time }),
  setNotCustomEndTime: (time) => set({ notCustomEndTime: time }),

  // Async Actions
  fetchUserData: async (id, form) => {
    if (!id) return;
    set({ userId: id });

    try {
      const result = await getUserById(id);
      const userData = result.data;
      const cleanMobile = format.mobileCleanup(userData.mobile || '');

      set({
        avatarUrl: userData.avatar_url || '',
        avatarType: userData.avatar_type || 'preset',
        refTfaOption: userData.tfa_option || false,
        initials: userData.name ? userData.name.charAt(0).toUpperCase() : 'U'
      });

      form.setFieldsValue({
        name: userData.name || '',
        username: userData.username || '',
        nickname: userData.nickname || '',
        email: userData.email || '',
        mobile: cleanMobile,
        jobTitle: userData.job_title || '',
        language: userData.language || 'en',
        tfa_option: userData.tfa_option || false,
        desktop_notif: userData.desktop_notification || false,
        email_notif: userData.email_notification || false,
        sms_notif: userData.sms_notification || false,
        text_optin: userData.text_optin === 'yes',
        active: userData.active || false,
      });
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  },

  fetchOfficeHours: async () => {
    try {
      const result = await getUserSettings();
      const settings = result.data;
      if (settings) {
        const updates = {};
        
        // Handle existing Interview Availability (only reading for now, not writing in this tab)
        if (settings.selectedDays && Array.isArray(settings.selectedDays)) {
          const days = [false, false, false, false, false, false, false];
          settings.selectedDays.forEach(dayIndex => {
            days[dayIndex] = true;
          });
          updates.selectedDays = days;
        }

        if (settings.startTime) {
          updates.startTime = moment(settings.startTime, 'hh:mm A');
        }
        if (settings.endTime) {
          updates.endTime = moment(settings.endTime, 'hh:mm A');
        }
        if (settings.zone) {
          updates.timezone = settings.zone;
        }

        // Office Hours Specifics
        updates.acknowledgmentEnabled = settings.acknowledgmentEnabled || false;
        updates.acknowledgmentMsg = settings.acknowledgmentMsg || "Thank you for your message. I'm out of the office right now, but will respond when I get back online."; 
        updates.scheduleMode = settings.scheduleMode || 'everyday';

        if(settings.notCustomStartTime) {
            updates.notCustomStartTime = moment(settings.notCustomStartTime, 'hh:mm A');
        }
        
        if(settings.notCustomEndTime) {
            updates.notCustomEndTime = moment(settings.notCustomEndTime, 'hh:mm A');
        }

        if(settings.availabilitySched) {
            updates.availabilitySched = settings.availabilitySched.map(sched => ({
                ...sched,
                startTime: moment(sched.startTime, 'hh:mm A'),
                endTime: moment(sched.endTime, 'hh:mm A')
            }));
        } else {
             // Fallback when the user has no persisted availabilitySched (e.g.
             // existing v1 users who never saved Office Hours). Mon–Fri 9–5,
             // weekends off — sensible values the backend custom-mode validator
             // will accept unchanged.
             const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
             updates.availabilitySched = days.map((day, idx) => ({
                 key: idx,
                 name: day,
                 startTime: moment('09:00', 'HH:mm'),
                 endTime: moment('17:00', 'HH:mm'),
                 isActive: idx >= 1 && idx <= 5
             }));
        }
        
        set(updates);
      }
    } catch (err) {
      console.log('Error loading office hours:', err);
    }
  },

  saveOfficeHours: async (messageApi) => {
    set({ officeHoursLoading: true });
    try {
      const {
          acknowledgmentEnabled,
          acknowledgmentMsg,
          scheduleMode,
          availabilitySched,
          notCustomStartTime,
          notCustomEndTime,
          userId
      } = get();

      // Block saving an invalid schedule (but never block disabling the feature).
      if (acknowledgmentEnabled) {
        const { hasError } = getOfficeHoursErrors({ scheduleMode, availabilitySched, notCustomStartTime, notCustomEndTime });
        if (hasError) {
          messageApi.error('Please fix the highlighted office hours before saving.');
          set({ officeHoursLoading: false });
          return;
        }
      }

      const transformedSched = availabilitySched.map(sched => ({
          key: sched.key,
          name: sched.name,
          startTime: sched.startTime.format('hh:mm A'),
          endTime: sched.endTime.format('hh:mm A'),
          isActive: sched.isActive
      }));

      const data = {
        acknowledgmentEnabled,
        acknowledgmentMsg,
        scheduleMode,
        availabilitySched: transformedSched,
        notCustomStartTime: notCustomStartTime.format('hh:mm A'),
        notCustomEndTime: notCustomEndTime.format('hh:mm A')
      };

      // Call new atomic endpoint
      await updateOfficeHours(userId, data); 
      messageApi.success('Office hours saved');

      // Dispatch event for Layout to update the top bar button
      window.dispatchEvent(new CustomEvent('officeHoursUpdated', { 
        detail: { acknowledgmentEnabled } 
      }));
    } catch (error) {
      console.error('Error saving office hours:', error);
      messageApi.error(error?.message || 'Failed to save office hours');
    } finally {
      set({ officeHoursLoading: false });
    }
  },

  uploadUserAvatar: async (file, messageApi) => {
    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    
    if (file.size > MAX_FILE_SIZE) {
      messageApi.error('File is too large. Maximum size is 5MB.');
      return false;
    }

    set({ uploading: true, uploadProgress: 0 });

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await uploadAvatar(formData, (progressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        set({ uploadProgress: percent });
      });

      const avatarUrlFromServer = res.data.data.avatar_url;
      const userData = session.get('userData');
      session.set('userData', { ...userData, avatar_url: avatarUrlFromServer, avatar_type: 'upload' });
      
      set({ 
        avatarUrl: avatarUrlFromServer, 
        avatarType: 'upload' 
      });

      window.dispatchEvent(new CustomEvent('avatarUpdated', { detail: avatarUrlFromServer }));
      messageApi.success('Avatar uploaded successfully');
    } catch (err) {
      console.error('Avatar upload failed', err);
      messageApi.error('Avatar upload failed');
    } finally {
      set({ uploading: false });
    }
    return false;
  },

  selectAvatarPreset: async (name, url, messageApi) => {
    set({ avatarSaving: true });
    const formData = new FormData();
    // Save the preset name instead of the hashed URL to ensure it doesn't break on rebuilds
    formData.append('avatar_url', name);
    formData.append('avatar_type', 'preset');

    try {
      await uploadAvatar(formData);
      
      set({ 
        avatarUrl: name, 
        avatarType: 'preset' 
      });
      
      const userData = session.get('userData');
      session.set('userData', { ...userData, avatar_url: name, avatar_type: 'preset' });

      // Dispatch event with the resolved URL for immediate UI feedback
      window.dispatchEvent(new CustomEvent('avatarUpdated', { detail: url }));
      messageApi.success('Avatar updated successfully');
    } catch (error) {
      messageApi.error('Failed to update avatar');
    } finally {
      set({ avatarSaving: false });
    }
  },

  saveSection: async (fields, sectionName, form, messageApi) => {
    try {
      const values = await form.validateFields(fields);
      set({ loading: true });

      const allValues = form.getFieldsValue();
      const mergedValues = { ...allValues, ...values };

      let rawMobile = mergedValues.mobile;
      if (typeof rawMobile === 'object' && rawMobile !== null) {
          const { countryCode, areaCode, phoneNumber } = rawMobile;
          rawMobile = `+${countryCode || 1}${areaCode || ''}${phoneNumber || ''}`;
      }
      let mobileNumber = format.mobileCleanup(rawMobile || '');
      if (mobileNumber && !mobileNumber.startsWith('+')) {
          mobileNumber = '+1' + mobileNumber;
      }
      const { userId, avatarUrl, avatarType } = get();

      const userUpdateDetails = {
        name: mergedValues.name,
        username: mergedValues.username,
        text_optin: mergedValues.text_optin ? 'yes' : 'no',
        mobile: mobileNumber || '',
        tfa_option: mergedValues.tfa_option ? 'yes' : 'no',
        desktop_notification: mergedValues.desktop_notif ? 'yes' : 'no',
        email_notification: mergedValues.email_notif ? 'yes' : 'no',
        sms_notification: mergedValues.sms_notif ? 'yes' : 'no',
        nickname: mergedValues.nickname,
        email: mergedValues.email,
        active: mergedValues.active ? 'yes' : 'no',
        job_title: mergedValues.jobTitle,
        avatar_url: avatarUrl,
        avatar_type: avatarType,
      };

      if (fields.includes('password') && values.password) {
        if (values.password !== values.verifypassword) {
          messageApi.error('Password verification does not match.');
          set({ loading: false });
          return;
        }
        
        // Use new auth endpoint for password changes
        await updateUserAuth(userId, { 
          password: values.password
        });
        
        form.resetFields(['password', 'verifypassword']);
      } else if (fields.includes('tfa_option') && sectionName === 'Security') {
         // Security section save without password change (just TFA)
         await updateUserAuth(userId, {
            tfa_option: mergedValues.tfa_option ? 'yes' : 'no'
         });
      } else {
         // Standard update for other sections
         await updateUser(userId, userUpdateDetails);
      }

      const userData = session.get('userData');
      // For auth updates we might not get full user object back or need to merge differently, 
      // but session user data usually keeps basic profile info. 
      // Password isn't in session. TFA option is.
      const updates = { ...userUpdateDetails };
      
      // update session with new tfa option if changed
      if (fields.includes('tfa_option')) {
         updates.tfa_option = mergedValues.tfa_option ? 'yes' : 'no';
      }

      session.set('userData', { ...userData, ...updates });

      messageApi.success(`${sectionName} saved successfully`);
      if (fields.includes('tfa_option')) {
        set({ refTfaOption: mergedValues.tfa_option });
      }
    } catch (error) {
      console.error(error);
      messageApi.error(error.message || `Failed to save ${sectionName}`);
    } finally {
      set({ loading: false });
    }
  },

  saveProfile: async (form, messageApi) => {
    const fields = ['name', 'nickname', 'email', 'mobile', 'jobTitle', 'language'];
    try {
      const values = await form.validateFields(fields);
      set({ loading: true });
      const { userId } = get();

      let rawMobile = values.mobile;
      if (typeof rawMobile === 'object' && rawMobile !== null) {
          const { countryCode, areaCode, phoneNumber } = rawMobile;
          rawMobile = `+${countryCode || 1}${areaCode || ''}${phoneNumber || ''}`;
      }
      let mobileNumber = format.mobileCleanup(rawMobile || '');
      if (mobileNumber && !mobileNumber.startsWith('+')) {
          mobileNumber = '+1' + mobileNumber;
      }

      const payload = {
        name: values.name,
        nickname: values.nickname,
        email: values.email,
        mobile: mobileNumber || '',
        job_title: values.jobTitle,
      };
      // ADR-035: the inbox renders messages in this language (session.userData.language)
      if (values.language) payload.language = values.language;

      await updateUserProfile(userId, payload);

      const userData = session.get('userData');
      session.set('userData', { ...userData, ...payload });

      window.dispatchEvent(new CustomEvent('profileUpdated', { detail: payload }));

      messageApi.success('Profile saved successfully');
    } catch (error) {
      console.error(error);
      const responseData = error;
      
      if (responseData && responseData.error === 'duplicate_email') {
          form.setFields([{
              name: 'email',
              value: form.getFieldValue('email'),
              validating: false,
              errors: [responseData.message]
          }]);
          form.scrollToField('email');
      } else if (responseData && responseData.message) {
         messageApi.error(responseData.message);
      } else {
         messageApi.error('Failed to save profile');
      }
    } finally {
      set({ loading: false });
    }
  },

  saveNotificationSettings: async (form, messageApi) => {
    const fields = ['desktop_notif', 'email_notif', 'sms_notif'];
    try {
      const values = await form.validateFields(fields);
      set({ loading: true });
      const { userId } = get();

      const payload = {
        desktop_notification: values.desktop_notif ? 'yes' : 'no',
        email_notification: values.email_notif ? 'yes' : 'no',
        sms_notification: values.sms_notif ? 'yes' : 'no',
      };

      await updateNotificationSettings(userId, payload);

      const userData = session.get('userData');
      // Update session with new notification settings
      const updates = {
          desktop_notification: payload.desktop_notification === 'yes', // store as boolean in session if that's how it was
          email_notification: payload.email_notification === 'yes',
          sms_notification: payload.sms_notification === 'yes'
      };
          
      session.set('userData', { 
          ...userData, 
          desktop_notification: values.desktop_notif,
          email_notification: values.email_notif,
          sms_notification: values.sms_notif 
      });

      messageApi.success('Preferences saved successfully');
    } catch (error) {
      console.error(error);
      messageApi.error('Failed to save preferences');
    } finally {
      set({ loading: false });
    }
  },
  
  // TFA Actions
  requestVerification: async (mobile, messageApi, form) => {
     set({ tfaLoading: true });
     
     // 2FA Refactor: Use mobile from session/store if not passed (inline flow)
     let mobileToUse = mobile;
     if (!mobileToUse) {
        const userData = session.get('userData');
        mobileToUse = userData ? userData.mobile : '';
     }
     
     let mobileNumber = format.mobileCleanup(mobileToUse || '');
     if (mobileNumber && !mobileNumber.startsWith('+')) {
        mobileNumber = '+1' + mobileNumber;
     }

     try {
        const res = await resources.requestVerification({ userNumber: mobileNumber });
        session.set('tfa_request_id', res.data);
        // Store full E.164 in session for resend
        session.set('tfa_mobile', mobileNumber);

        set({
           tfaMobile: mobileNumber,
           tfaRequestId: res.data,
           tfaRequestModal: true, // Used as inline visibility flag now
           tfaCheckModal: false,
           tfaLoading: false
        });
     } catch (err) {
        set({ tfaMobile: '', tfaRequestModal: false });
        // We'll need refTfaOption from store
        const { refTfaOption } = get();
        form.setFieldsValue({ tfa_option: refTfaOption });
        
        const errorMessage = err.message || err.response?.data?.message || constants.tfa.request.requestFailed;
        messageApi.error(errorMessage);
        set({ tfaLoading: false });
     }
  },

  checkVerification: async (messageApi, form) => {
    set({ tfaLoading: true });
    const { tfaRequestId, tfaCode, tfaMobile } = get();

    try {
        const res = await resources.checkVerification({ requestId: tfaRequestId, code: tfaCode });
        
        if (res.data.status === '0') {
          // Verify success - Save 2FA enabled state to backend
          const { userId } = get();
          try {
             await updateUserAuth(userId, { tfa_option: 'yes' });
             messageApi.success(constants.tfa.check.checkSuccess);
             
             set({ 
               refTfaOption: true,
               tfaCheckModal: false, // Reusing this for inline visibility state if needed, or just clear it
               tfaCode: '',
               tfaRequestId: ''
             });
             form.setFieldsValue({ tfa_option: true });
             
             // Update session user data
             const userData = session.get('userData');
             session.set('userData', { ...userData, tfa_option: true });
             
             // Refresh page as requested
             window.location.reload();

          } catch (err) {
             messageApi.error('Failed to enable 2FA setting');
          }
          
        } else if (res.data.status === '16') {
          messageApi.error(constants.tfa.check.notMatch);
          set({ tfaLoading: false });
        } else {
          messageApi.error(constants.tfa.check.checkFailed);
          get().clearTfaState(form);
        }
    } catch {
       messageApi.error(constants.tfa.check.checkFailed);
       get().clearTfaState(form);
    }
  },

  cancelVerification: async (messageApi, form) => {
    set({ tfaLoading: true });
    const { tfaRequestId } = get();

    try {
      const res = await resources.cancelVerification({ requestId: tfaRequestId });
      if (res.data.status === '0') {
         messageApi.success(constants.tfa.cancellation.cancelSuccess);
      } else if (res.data.status === '19') {
         messageApi.warning(constants.tfa.cancellation.noEarlyCancel);
         set({ tfaLoading: false });
         return;
      } else if (res.data.status === '6') {
         messageApi.info(constants.tfa.cancellation.expiredRequest);
      } else {
         messageApi.error(constants.tfa.cancellation.cancelFailed);
      }
      get().clearTfaState(form);
    } catch {
      messageApi.error(constants.tfa.cancellation.cancelFailed);
      get().clearTfaState(form);
    }
  },

  resendVerification: async (messageApi, form) => {
    set({ tfaLoading: true });
    const { tfaRequestId } = get();

    try {
      const res = await resources.cancelVerification({ requestId: tfaRequestId });
      if (res.data.status === '0' || res.data.status === '6') {
          set({ tfaCode: '', tfaRequestId: '' });
          session.unset('tfa_request_id');

          session.unset('tfa_request_id');

          const mobileNumber = session.get('tfa_mobile');
          // mobileNumber is already E.164 in session from requestVerification
          
          try {
             const reqRes = await resources.requestVerification({ userNumber: mobileNumber });
             session.set('tfa_request_id', reqRes.data);
             set({
               tfaRequestId: reqRes.data,
               tfaLoading: false,
               resetTfaTimer: true
             });
             messageApi.success('Verification code resent');
          } catch {
             messageApi.error(constants.tfa.request.requestFailed);
             get().clearTfaState(form);
          }
      } else if (res.data.status === '19') {
         messageApi.warning(constants.tfa.cancellation.noEarlyCancel);
         set({ tfaLoading: false });
      } else {
         messageApi.error(constants.tfa.resend.resendFailed);
         get().clearTfaState(form);
      }
    } catch {
      messageApi.error(constants.tfa.resend.resendFailed);
      get().clearTfaState(form);
    }
  },

  disableTwoFactor: async (messageApi, form) => {
      set({ tfaLoading: true });
      const { userId } = get();
      try {
         await updateUserAuth(userId, { tfa_option: 'no' });
         
         const userData = session.get('userData');
         session.set('userData', { ...userData, tfa_option: false });
         
         set({ refTfaOption: false, tfaLoading: false });
         form.setFieldsValue({ tfa_option: false });
         
         messageApi.success('Two-factor authentication disabled');
         setTimeout(() => {
             window.location.reload();
         }, 1000);
         
      } catch (error) {
         set({ tfaLoading: false });
         const { refTfaOption } = get();
         form.setFieldsValue({ tfa_option: refTfaOption });
         messageApi.error('Failed to disable 2FA');
      }
  },

  clearTfaState: (form) => {
    const { refTfaOption } = get();
    set({
      tfaCode: '',
      tfaRequestId: '',
      tfaMobile: '',
      tfaCheckModal: false,
      tfaRequestModal: false,
      tfaLoading: false,
      resetTfaTimer: false,
    });

    form.setFieldsValue({ tfa_option: refTfaOption });
    session.unset('tfa_request_id');
    session.unset('tfa_mobile');
  }

}));

export default useProfileStore;
